<template>
  <section class="flex w-full flex-col gap-2 sm:gap-4" data-testid="contract-improvement">
    <div class="flex flex-col gap-5 rounded-xl border border-gray-200 bg-white p-4 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] sm:p-6 lg:p-8">
      <div v-if="isPersisted" class="flex items-start gap-3">
        <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EAF8FB] text-[#25B0D7]">
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 2 4.5 13H11l-1 9 8.5-11H12l1-9Z" />
          </svg>
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-1.5">
            <h3 class="text-base font-semibold text-[#385660] sm:text-lg">{{ t('contractImprovement.heading') }}</h3>
            <span
              class="info-tooltip"
              tabindex="0"
              :aria-label="t('contractImprovement.infoLabel')"
              data-testid="contract-improvement-info"
            >
              <svg class="info-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z" />
              </svg>
              <span class="tooltip-text" role="tooltip">{{ t('contractImprovement.info') }}</span>
            </span>
            <span v-if="isPersisted" class="inline-flex items-center gap-1 rounded-full bg-[#EAF8FB] px-2.5 py-1 text-xs font-semibold text-[#147D98]">
              <svg class="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M16.704 5.292a1 1 0 0 1 .004 1.414l-7.5 7.542a1 1 0 0 1-1.416.002l-4-4a1 1 0 0 1 1.416-1.414l3.29 3.29 6.792-6.83a1 1 0 0 1 1.414-.004Z" clip-rule="evenodd" />
              </svg>
              {{ t('contractImprovement.filter.accepted') }}
            </span>
          </div>
        </div>
      </div>

      <label
        v-if="!isPersisted"
        class="flex items-start gap-3"
        :class="disabled || readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'"
      >
        <input
          type="checkbox"
          class="mt-0.5 h-5 w-5 shrink-0 accent-[#25B0D7]"
          :checked="accepted"
          :disabled="disabled || readOnly || isSaving"
          data-testid="contract-improvement-check"
          @change="onToggle($event.target.checked)"
        />
        <span>
          <span class="block font-semibold text-[#385660]">{{ t('contractImprovement.check') }}. {{ t('contractImprovement.checkHelp') }}</span>
        </span>
      </label>

      <div
        v-else
        class="grid gap-3 rounded-xl border border-[#B9E5EF] bg-[#F4FBFD] p-4 sm:grid-cols-2"
        data-testid="contract-improvement-saved"
      >
        <div>
          <span class="block text-xs font-semibold uppercase tracking-wide text-[#7CADB9]">{{ t('contractImprovement.acceptance') }}</span>
          <span class="mt-1 block font-semibold text-[#385660]">{{ acceptedSummary }}</span>
        </div>
        <div v-if="originalTypeLabel">
          <span class="block text-xs font-semibold uppercase tracking-wide text-[#7CADB9]">{{ t('contractImprovement.source') }}</span>
          <span class="mt-1 block font-semibold text-[#385660]">{{ originalTypeLabel }}</span>
        </div>
      </div>

      <div v-if="accepted" class="border-t border-gray-200 pt-5" data-testid="contract-improvement-documents">
        <div class="mb-4">
          <h4 class="font-semibold text-[#385660]">{{ t('contractImprovement.requiredDocuments') }}</h4>
          <p class="mt-1 text-sm text-gray-500">
            {{ t(readOnly ? 'contractImprovement.documentsStoredHelp' : (isPersisted ? 'contractImprovement.replaceHelp' : 'contractImprovement.documentsHelp')) }}
          </p>
        </div>

        <div class="grid gap-3 md:grid-cols-2">
          <div v-for="doc in documents" :key="doc.key" class="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div class="flex items-start gap-3">
              <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-[#25B0D7] shadow-sm">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8m-6-6 6 6m-6-6v6h6M8 13h8M8 17h5" />
                </svg>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <span class="font-medium text-[#385660]">{{ t(`contractImprovement.documents.${doc.key}`) }}</span>
                  <span class="text-xs font-semibold" :class="documentReady(doc) ? 'text-[#147D98]' : 'text-amber-700'">
                    {{ t(doc.key === 'bank' && bankCredentialReady && !doc.saved && !doc.selected ? 'contractImprovement.bankNotRequired' : (documentReady(doc) ? 'contractImprovement.documentReady' : 'contractImprovement.documentPending')) }}
                  </span>
                </div>
                <p class="mt-1 truncate text-sm text-gray-500" :title="doc.selected || doc.saved">
                  {{ doc.selected || doc.saved || t('contractImprovement.noFile') }}
                </p>
                <template v-if="!readOnly">
                  <input
                    :ref="(element) => setFileInput(doc.key, element)"
                    type="file"
                    class="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    :disabled="disabled || isSaving"
                    :data-testid="`contract-improvement-${doc.key}`"
                    @change="onPick(doc.key, $event)"
                  />
                  <button
                    type="button"
                    class="mt-3 inline-flex rounded-lg border border-[#147D98] bg-white px-3 py-2 text-sm font-semibold text-[#147D98] transition hover:bg-[#EAF8FB]"
                    :class="disabled || isSaving ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'"
                    :disabled="disabled || isSaving"
                    @click="openFilePicker(doc.key)"
                  >
                    {{ t(doc.saved ? 'contractImprovement.replaceFile' : 'contractImprovement.chooseFile') }}
                  </button>
                </template>
                <button
                  v-else-if="doc.id"
                  type="button"
                  class="mt-3 inline-flex items-center gap-2 rounded-lg border border-[#25B0D7] bg-white px-3 py-2 text-sm font-semibold text-[#25B0D7] transition hover:bg-[#EAF8FB] disabled:cursor-wait disabled:opacity-50"
                  :disabled="Boolean(downloadingDocument)"
                  :data-testid="`contract-improvement-download-${doc.key}`"
                  @click="downloadDocument(doc)"
                >
                  <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" />
                  </svg>
                  {{ t(downloadingDocument === doc.key ? 'contractImprovement.downloading' : 'contractImprovement.downloadFile') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-4" data-testid="contract-improvement-iban">
          <label class="block font-medium text-[#385660]" for="contract-improvement-iban-input">
            {{ t('contractImprovement.ibanLabel') }}
          </label>
          <p class="mt-1 text-sm text-gray-500">{{ t('contractImprovement.ibanHelp') }}</p>
          <input
            v-if="!readOnly"
            id="contract-improvement-iban-input"
            v-model="iban"
            type="text"
            autocomplete="off"
            :disabled="disabled || isSaving"
            :placeholder="t('contractImprovement.ibanPlaceholder')"
            class="mt-3 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-[#385660] outline-none focus:border-[#25B0D7] disabled:opacity-50"
            @input="onIbanInput"
          />
          <p v-if="readOnly || requestData?.contract_improvement_bank_iban_provided" class="mt-2 font-semibold text-[#385660]">
            {{ requestData?.contract_improvement_bank_iban || (requestData?.contract_improvement_bank_file ? t('contractImprovement.bankDocumentProvided') : '-') }}
          </p>
        </div>
        <p v-if="!readOnly" class="mt-3 text-xs text-gray-500">{{ t('contractImprovement.formats') }}</p>
      </div>

      <div v-if="isPersisted && readOnly" class="border-t border-gray-200 pt-5" data-testid="contract-improvement-workflow">
        <h4 class="font-semibold text-[#385660]">{{ t('contractImprovement.workflow.title') }}</h4>
        <p class="mt-1 text-sm text-gray-500">{{ t('contractImprovement.workflow.help') }}</p>
        <div class="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
          <label class="flex-1 text-sm font-medium text-[#385660]">
            {{ t('contractImprovement.workflow.status') }}
            <select v-model="workflowStatus" :disabled="!canManageWorkflow || isSavingWorkflow"
              class="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 disabled:opacity-60">
              <option :value="currentWorkflowStatus">{{ workflowLabel(currentWorkflowStatus) }}</option>
              <option v-for="status in availableWorkflowStatuses" :key="status" :value="status">{{ workflowLabel(status) }}</option>
            </select>
          </label>
          <button v-if="canManageWorkflow" type="button" :disabled="isSavingWorkflow || workflowStatus === currentWorkflowStatus"
            class="rounded-lg bg-[#25B0D7] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            data-testid="contract-improvement-workflow-save" @click="saveWorkflow">
            {{ t('contractImprovement.workflow.save') }}
          </button>
        </div>
        <textarea v-if="canManageWorkflow && workflowStatus === 'rejected'" v-model="rejectionReason" rows="3"
          class="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" :placeholder="t('contractImprovement.workflow.rejectionReason')" />
        <p v-if="requestData?.contract_improvement_rejection_reason" class="mt-3 text-sm text-red-700">
          {{ requestData.contract_improvement_rejection_reason }}
        </p>
      </div>

      <p v-if="errorMessage" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="contract-improvement-error">
        {{ errorMessage }}
      </p>

      <div v-if="!disabled && !readOnly && accepted" class="flex justify-end border-t border-gray-200 pt-5">
        <button
          type="button"
          class="w-full rounded-lg bg-[#25B0D7] px-5 py-2.5 text-sm font-semibold text-white shadow transition hover:bg-[#229ABB] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          :disabled="isSaving || !isDirty"
          data-testid="contract-improvement-save"
          @click="save"
        >
          {{ isSaving ? t('contractImprovement.saving') : t(isPersisted ? 'contractImprovement.updateDocuments' : 'contractImprovement.confirm') }}
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { isValidIban, normalizeIban } from '@/services/contractImprovement';

const props = defineProps({
  requestData: { type: Object, default: null },
  disabled: { type: Boolean, default: false },
  readOnly: { type: Boolean, default: false },
  canManageWorkflow: { type: Boolean, default: false },
});
const emit = defineEmits(['saved']);

const { t, locale } = useI18n();

const accepted = ref(false);
const isSaving = ref(false);
const downloadingDocument = ref('');
const isDirty = ref(false);
const errorMessage = ref('');
const picked = ref({ dni: null, bank: null });
const iban = ref('');
const workflowStatus = ref('accepted');
const rejectionReason = ref('');
const isSavingWorkflow = ref(false);
const fileInputs = new Map();

const isPersisted = computed(() => Boolean(props.requestData?.contract_improvement_accepted_at));
const savedBy = computed(
  () => props.requestData?.contract_improvement_acceptor?.name
    || props.requestData?.contract_improvement_acceptor?.username
    || '-'
);

// Un documento ya guardado no hay que volver a adjuntarlo: se muestra su nombre
// y solo se envía el fichero si el cliente elige uno nuevo.
const documents = computed(() => [
  {
    key: 'dni',
    id: props.requestData?.contract_improvement_dni_file?.id || null,
    saved: props.requestData?.contract_improvement_dni_file?.file_name || '',
    selected: picked.value.dni?.name || '',
  },
  {
    key: 'bank',
    id: props.requestData?.contract_improvement_bank_file?.id || null,
    saved: props.requestData?.contract_improvement_bank_file?.file_name || '',
    selected: picked.value.bank?.name || '',
  },
]);

const normalizedIban = computed(() => normalizeIban(iban.value));
const currentWorkflowStatus = computed(() => props.requestData?.contract_improvement_status || 'accepted');
const transitions = {
  accepted: ['review', 'rejected'],
  review: ['in_management', 'rejected'],
  in_management: ['review', 'completed', 'rejected'],
  rejected: ['review'],
  completed: [],
};
const availableWorkflowStatuses = computed(() => transitions[currentWorkflowStatus.value] || []);
const workflowLabel = (status) => t(`contractImprovement.workflow.statuses.${status}`);

const bankCredentialReady = computed(() => Boolean(
  picked.value.bank
  || props.requestData?.contract_improvement_bank_file
  || props.requestData?.contract_improvement_bank_iban_provided
  || (normalizedIban.value && isValidIban(normalizedIban.value))
));
const documentReady = (doc) => doc.key === 'bank' ? bankCredentialReady.value : Boolean(doc.saved || doc.selected);

const acceptedSummary = computed(() => {
  const value = props.requestData?.contract_improvement_accepted_at;
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T'));
  const formatted = Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(locale.value === 'ca' ? 'ca-ES' : 'es-ES', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(date);
  return t('contractImprovement.acceptedOn', { date: formatted, user: savedBy.value });
});

const originalTypeLabel = computed(() => {
  if (!isPersisted.value) return '';
  const type = props.requestData?.contract_improvement_original_type || props.requestData?.type;
  if (!type) return '';
  const base = t(`common.requestTypes.${type}`);
  const action = props.requestData?.contract_improvement_original_action || props.requestData?.other_action;
  return action ? `${base} - ${t(`common.requestTypes.${action}`)}` : base;
});

const syncFromRequest = () => {
  accepted.value = Boolean(props.requestData?.contract_improvement_accepted_at);
  picked.value = { dni: null, bank: null };
  iban.value = '';
  workflowStatus.value = props.requestData?.contract_improvement_status || 'accepted';
  rejectionReason.value = '';
  isDirty.value = false;
  errorMessage.value = '';
};
watch(() => props.requestData?.id, syncFromRequest, { immediate: true });
watch(() => props.requestData?.contract_improvement_accepted_at, syncFromRequest);

const onToggle = (value) => {
  if (isPersisted.value) return;
  accepted.value = value;
  if (!value) picked.value = { dni: null, bank: null };
  isDirty.value = value;
  errorMessage.value = '';
};

const setFileInput = (key, element) => {
  if (element) fileInputs.set(key, element);
  else fileInputs.delete(key);
};

const openFilePicker = (key) => {
  if (props.disabled || isSaving.value) return;
  fileInputs.get(key)?.click();
};

const onPick = (key, event) => {
  picked.value[key] = event.target.files?.[0] || null;
  isDirty.value = isPersisted.value
    ? Boolean(picked.value.dni || picked.value.bank || normalizedIban.value)
    : accepted.value;
  errorMessage.value = '';
};

const onIbanInput = () => {
  isDirty.value = Boolean(normalizedIban.value) || Boolean(picked.value.dni || picked.value.bank) || (!isPersisted.value && accepted.value);
  errorMessage.value = '';
};

/** Falta la documentación que ni se acaba de elegir ni está ya guardada. */
const missingDocuments = () => {
  const missing = [];
  if (!picked.value.dni && !props.requestData?.contract_improvement_dni_file) missing.push(t('contractImprovement.documents.dni'));
  if (!bankCredentialReady.value) missing.push(t('contractImprovement.bankAlternative'));
  return missing;
};

const downloadDocument = async (doc) => {
  if (!doc.id || downloadingDocument.value) return;

  downloadingDocument.value = doc.key;
  errorMessage.value = '';

  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/request/file/${doc.id}/download`,
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        responseType: 'blob',
      }
    );
    const blob = new Blob([response.data], {
      type: response.headers?.['content-type'] || undefined,
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = doc.saved || 'documento';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  } catch {
    errorMessage.value = t('contractImprovement.errors.download');
  } finally {
    downloadingDocument.value = '';
  }
};

const save = async () => {
  errorMessage.value = '';

  if (normalizedIban.value && !isValidIban(normalizedIban.value)) {
    errorMessage.value = t('contractImprovement.errors.iban');
    return;
  }

  if (accepted.value) {
    const missing = missingDocuments();
    if (missing.length) {
      errorMessage.value = t('contractImprovement.errors.missing', { documents: missing.join(', ') });
      return;
    }
  }

  isSaving.value = true;
  try {
    const payload = new FormData();
    payload.append('accepted', accepted.value ? '1' : '0');
    if (picked.value.dni) payload.append('dni_file', picked.value.dni);
    if (picked.value.bank) payload.append('bank_file', picked.value.bank);
    if (normalizedIban.value) payload.append('iban', normalizedIban.value);

    await axios.post(
      `${import.meta.env.VITE_API_URL}/request/contract-improvement/${props.requestData.id}`,
      payload,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    );
    isDirty.value = false;
    emit('saved');
  } catch (error) {
    const errors = error?.response?.data?.errors;
    errorMessage.value = errors
      ? Object.values(errors).flat().join(' ')
      : (error?.response?.data?.value || t('contractImprovement.errors.generic'));
  } finally {
    isSaving.value = false;
  }
};

const saveWorkflow = async () => {
  errorMessage.value = '';
  if (workflowStatus.value === 'rejected' && !rejectionReason.value.trim()) {
    errorMessage.value = t('contractImprovement.workflow.rejectionRequired');
    return;
  }
  isSavingWorkflow.value = true;
  try {
    await axios.put(
      `${import.meta.env.VITE_API_URL}/request/contract-improvement/${props.requestData.id}/status`,
      { status: workflowStatus.value, rejection_reason: rejectionReason.value.trim() || null },
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    );
    emit('saved');
  } catch (error) {
    errorMessage.value = Object.values(error?.response?.data?.errors || {}).flat().join(' ')
      || error?.response?.data?.value
      || t('contractImprovement.errors.generic');
  } finally {
    isSavingWorkflow.value = false;
  }
};
</script>

<style scoped>
.info-tooltip {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  color: var(--c-primary, #25b0d7);
  cursor: help;
}

.info-icon {
  width: 16px;
  height: 16px;
}

.tooltip-text {
  display: none;
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  max-width: 240px;
  background-color: #385660;
  color: #fff;
  font-size: 12px;
  line-height: 1.4;
  padding: 6px 10px;
  border-radius: 6px;
  white-space: normal;
  z-index: 20;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

.info-tooltip:hover .tooltip-text,
.info-tooltip:focus .tooltip-text,
.info-tooltip:focus-visible .tooltip-text {
  display: block;
}
</style>
